import { useState, useEffect, useCallback } from 'react';
import { Lock, LogOut, Mail, Phone, Briefcase, Wallet, Clock, Trash2, ArrowLeft, Search, FileText, Plus, Pencil, X, Save, ChevronDown, ChevronUp, Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Heading2, Heading3, Link2, Quote, Code, Undo2, Redo2, Package, Tag, Scale, CreditCard, Users, MessageSquare, BarChart3, Home, ShieldAlert, Sun, Moon } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import ProductManager from './admin/ProductManager';
import { useTheme } from '@/hooks/useTheme';
import OrderManager from './admin/OrderManager';
import DiscountManager from './admin/DiscountManager';
import LegalManager from './admin/LegalManager';
import PricingManager from './admin/PricingManager';
import AccountManager from './admin/AccountManager';
import ContactFormManager from './admin/ContactFormManager';
import AnalyticsManager from './admin/AnalyticsManager';

const API = process.env.REACT_APP_BACKEND_URL;

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Login failed');
      localStorage.setItem('admin_token', data.token);
      onLogin(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 relative z-[1]">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#00E5FF] mb-4">
            <span className="font-['Outfit'] font-black text-[#0A0A0A] text-lg">Ui</span>
          </div>
          <h1 className="font-['Outfit'] text-2xl font-bold text-white">Admin Panel</h1>
          <p className="text-sm text-slate-500 mt-1">Sign in to manage your site</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1.5">Email</label>
            <input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-[#00E5FF]/50" placeholder="Email" required />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1.5">Password</label>
            <input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-[#00E5FF]/50" placeholder="Enter password" required />
          </div>
          {error && <p data-testid="admin-login-error" className="text-xs text-rose-400 bg-rose-500/10 rounded-lg px-3 py-2">{error}</p>}
          <button data-testid="admin-login-btn" type="submit" disabled={loading} className="w-full bg-[#00E5FF] text-[#0A0A0A] rounded-xl py-3 text-sm font-semibold hover:bg-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            <Lock className="w-4 h-4" />
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

function getTimeAgo(date) {
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function SubmissionCard({ sub, onDelete }) {
  const date = new Date(sub.created_at);
  return (
    <div data-testid="submission-card" className="bg-[#111111] border border-white/5 rounded-xl p-5 hover:bg-white/[0.03]">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-['Outfit'] text-base font-semibold text-white">{sub.name}</h3>
          <div className="flex items-center gap-1.5 mt-0.5">
            <Clock className="w-3 h-3 text-slate-600" />
            <span className="text-xs text-slate-500">{getTimeAgo(date)}</span>
          </div>
        </div>
        <button data-testid="delete-submission-btn" onClick={() => onDelete(sub.id)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-600 hover:text-rose-400 hover:bg-rose-500/10 transition-colors">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-sm">
          <Mail className="w-3.5 h-3.5 text-[#00E5FF]" />
          <a href={`mailto:${sub.email}`} className="text-[#00B4D8] hover:underline">{sub.email}</a>
        </div>
        {sub.phone && <div className="flex items-center gap-2 text-sm"><Phone className="w-3.5 h-3.5 text-slate-500" /><span className="text-slate-400">{sub.phone}</span></div>}
        {sub.service && <div className="flex items-center gap-2 text-sm"><Briefcase className="w-3.5 h-3.5 text-slate-500" /><span className="text-slate-400">{sub.service}</span></div>}
        {sub.budget && <div className="flex items-center gap-2 text-sm"><Wallet className="w-3.5 h-3.5 text-slate-500" /><span className="text-slate-400">{sub.budget}</span></div>}
        {sub.care_plan && <div className="flex items-center gap-2 text-sm"><Tag className="w-3.5 h-3.5 text-slate-500" /><span className="text-slate-400">{sub.care_plan}</span></div>}
      </div>
      <div className="bg-white/5 rounded-lg p-3 border border-white/5">
        <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{sub.message}</p>
      </div>
      {sub.consent && (
        <details className="mt-3">
          <summary className="text-[10px] font-medium text-slate-600 cursor-pointer hover:text-slate-400 transition-colors">Consent Record</summary>
          <div className="mt-2 bg-white/[0.02] border border-white/5 rounded-lg p-3 space-y-1">
            <p className="text-[11px] text-slate-500"><span className="text-slate-400">Timestamp:</span> {sub.consent.timestamp}</p>
            <p className="text-[11px] text-slate-500"><span className="text-slate-400">IP:</span> {sub.consent.ip_address}</p>
            <p className="text-[11px] text-slate-500"><span className="text-slate-400">Terms Version:</span> {sub.consent.terms_version}</p>
            <p className="text-[11px] text-slate-500"><span className="text-slate-400">Privacy Version:</span> {sub.consent.privacy_version}</p>
            <p className="text-[11px] font-mono text-emerald-400/80">{sub.consent.consent_string}</p>
          </div>
        </details>
      )}
    </div>
  );
}

/* ========== Rich Text Toolbar Button ========== */
function ToolbarBtn({ onClick, active, children, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
        active ? 'bg-[#00E5FF]/20 text-[#00E5FF]' : 'text-slate-400 hover:text-white hover:bg-white/10'
      }`}
    >
      {children}
    </button>
  );
}

function ToolbarDivider() {
  return <div className="w-px h-5 bg-white/10 mx-1" />;
}

/* ========== Blog Editor ========== */
function BlogEditor({ post, onSave, onCancel }) {
  const [title, setTitle] = useState(post?.title || '');
  const [slug, setSlug] = useState(post?.slug || '');
  const [excerpt, setExcerpt] = useState(post?.excerpt || '');
  const [tag, setTag] = useState(post?.tag || '');
  const [keywords, setKeywords] = useState(post?.keywords || '');
  const [readTime, setReadTime] = useState(post?.read_time || 5);
  const [publishedAt, setPublishedAt] = useState(
    post?.published_at ? new Date(post.published_at).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showHtml, setShowHtml] = useState(false);
  const [rawHtml, setRawHtml] = useState(post?.content || '');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-[#00B4D8] underline' },
      }),
      Underline,
    ],
    content: post?.content || '',
    editorProps: {
      attributes: {
        class: 'prose prose-invert prose-sm max-w-none min-h-[300px] focus:outline-none px-4 py-3 prose-headings:font-["Outfit"] prose-p:leading-relaxed prose-a:text-[#00B4D8]',
      },
    },
    onUpdate: ({ editor: e }) => {
      setRawHtml(e.getHTML());
    },
  });

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url && editor) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  const handleSave = async () => {
    const finalContent = showHtml ? rawHtml : (editor ? editor.getHTML() : '');
    if (!title.trim() || !finalContent.trim() || finalContent === '<p></p>') {
      setError('Title and content are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('admin_token');
      const body = { title, slug, excerpt, content: finalContent, tag, keywords, read_time: readTime, published_at: new Date(publishedAt).toISOString() };
      const url = post?.id ? `${API}/api/admin/blog/${post.id}` : `${API}/api/admin/blog`;
      const method = post?.id ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save');
      onSave(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleHtmlView = () => {
    if (showHtml && editor) {
      editor.commands.setContent(rawHtml);
    } else if (editor) {
      setRawHtml(editor.getHTML());
    }
    setShowHtml(!showHtml);
  };

  return (
    <div className="bg-[#111111] border border-white/10 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-['Outfit'] text-lg font-bold text-white">{post?.id ? 'Edit Post' : 'New Post'}</h3>
        <button onClick={onCancel} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-white hover:bg-white/10 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-xs font-medium text-slate-400 block mb-1.5">Title</label>
          <input data-testid="blog-editor-title" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50" placeholder="Post title" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1.5">Slug</label>
            <input data-testid="blog-editor-slug" value={slug} onChange={(e) => setSlug(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50" placeholder="auto-generated-from-title" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1.5">Tag</label>
            <input value={tag} onChange={(e) => setTag(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50" placeholder="e.g. SEO, Pricing" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1.5">Read time (mins)</label>
            <input type="number" value={readTime} onChange={(e) => setReadTime(parseInt(e.target.value) || 5)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50" />
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-400 block mb-1.5">Published Date</label>
          <input data-testid="blog-editor-date" type="date" value={publishedAt} onChange={(e) => setPublishedAt(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50" />
        </div>

        <div>
          <label className="text-xs font-medium text-slate-400 block mb-1.5">Excerpt</label>
          <textarea value={excerpt} onChange={(e) => setExcerpt(e.target.value)} rows={2} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50 resize-none" placeholder="Short description for blog listing" />
        </div>

        <div>
          <label className="text-xs font-medium text-slate-400 block mb-1.5">Keywords</label>
          <input value={keywords} onChange={(e) => setKeywords(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50" placeholder="SEO keywords, comma separated" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-medium text-slate-400">Content</label>
            <button type="button" onClick={toggleHtmlView} className="text-[10px] font-medium text-slate-500 hover:text-[#00E5FF] transition-colors px-2 py-1 rounded-md bg-white/5 border border-white/10">
              {showHtml ? 'Rich Editor' : 'HTML View'}
            </button>
          </div>

          {showHtml ? (
            <textarea
              data-testid="blog-editor-html"
              value={rawHtml}
              onChange={(e) => setRawHtml(e.target.value)}
              rows={14}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm font-mono focus:outline-none focus:border-[#00E5FF]/50 resize-y"
              placeholder="<h2>Your heading</h2><p>Your content...</p>"
            />
          ) : editor ? (
            <div className="border border-white/10 rounded-xl overflow-hidden bg-white/5">
              {/* Toolbar */}
              <div data-testid="blog-editor-toolbar" className="flex items-center flex-wrap gap-0.5 px-2 py-1.5 border-b border-white/10 bg-white/[0.02]">
                <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold">
                  <Bold className="w-3.5 h-3.5" />
                </ToolbarBtn>
                <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic">
                  <Italic className="w-3.5 h-3.5" />
                </ToolbarBtn>
                <ToolbarBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline">
                  <UnderlineIcon className="w-3.5 h-3.5" />
                </ToolbarBtn>

                <ToolbarDivider />

                <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2">
                  <Heading2 className="w-3.5 h-3.5" />
                </ToolbarBtn>
                <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3">
                  <Heading3 className="w-3.5 h-3.5" />
                </ToolbarBtn>

                <ToolbarDivider />

                <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List">
                  <List className="w-3.5 h-3.5" />
                </ToolbarBtn>
                <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered List">
                  <ListOrdered className="w-3.5 h-3.5" />
                </ToolbarBtn>

                <ToolbarDivider />

                <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Quote">
                  <Quote className="w-3.5 h-3.5" />
                </ToolbarBtn>
                <ToolbarBtn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Code Block">
                  <Code className="w-3.5 h-3.5" />
                </ToolbarBtn>
                <ToolbarBtn onClick={addLink} active={editor.isActive('link')} title="Link">
                  <Link2 className="w-3.5 h-3.5" />
                </ToolbarBtn>

                <ToolbarDivider />

                <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} title="Undo">
                  <Undo2 className="w-3.5 h-3.5" />
                </ToolbarBtn>
                <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} title="Redo">
                  <Redo2 className="w-3.5 h-3.5" />
                </ToolbarBtn>
              </div>

              {/* Editor */}
              <EditorContent data-testid="blog-editor-content" editor={editor} />
            </div>
          ) : null}
        </div>

        {error && <p className="text-xs text-rose-400 bg-rose-500/10 rounded-lg px-3 py-2">{error}</p>}

        <div className="flex gap-3 pt-2">
          <button data-testid="blog-editor-save" onClick={handleSave} disabled={saving} className="inline-flex items-center gap-2 bg-[#00E5FF] text-[#0A0A0A] px-6 py-3 rounded-xl text-sm font-semibold hover:bg-white transition-colors disabled:opacity-50">
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : (post?.id ? 'Update Post' : 'Publish Post')}
          </button>
          <button onClick={onCancel} className="px-6 py-3 rounded-xl text-sm font-medium text-slate-400 border border-white/10 hover:text-white hover:border-white/20 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function BlogPostRow({ post, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const date = new Date(post.published_at);

  return (
    <div data-testid="blog-post-row" className="bg-[#111111] border border-white/5 rounded-xl hover:bg-white/[0.03]">
      <div className="p-4 flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {post.tag && <span className="text-[9px] font-bold uppercase tracking-widest text-[#00E5FF] bg-[#00E5FF]/10 px-2 py-0.5 rounded-full">{post.tag}</span>}
            <span className="text-xs text-slate-500">{date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
          </div>
          <h3 className="font-['Outfit'] text-sm font-semibold text-white truncate">{post.title}</h3>
          <p className="text-xs text-slate-500 truncate mt-0.5">/blog/{post.slug}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => setExpanded(!expanded)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-white hover:bg-white/10 transition-colors">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button data-testid="blog-edit-btn" onClick={() => onEdit(post)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-[#00E5FF] hover:bg-[#00E5FF]/10 transition-colors">
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button data-testid="blog-delete-btn" onClick={() => onDelete(post.id)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 border-t border-white/5 pt-3">
          <p className="text-xs text-slate-400 mb-2"><strong className="text-slate-300">Excerpt:</strong> {post.excerpt || '—'}</p>
          <p className="text-xs text-slate-400 mb-2"><strong className="text-slate-300">Keywords:</strong> {post.keywords || '—'}</p>
          <p className="text-xs text-slate-400"><strong className="text-slate-300">Read time:</strong> {post.read_time} min</p>
        </div>
      )}
    </div>
  );
}

function BlogManager({ onLogout }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list, {} = new, {id:...} = edit

  const fetchPosts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/blog/posts`);
      const data = await res.json();
      setPosts(data);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    const token = localStorage.getItem('admin_token');
    await fetch(`${API}/api/admin/blog/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    setPosts(p => p.filter(post => post.id !== id));
  };

  const handleSave = (savedPost) => {
    if (editing?.id) {
      setPosts(p => p.map(post => post.id === savedPost.id ? savedPost : post));
    } else {
      setPosts(p => [savedPost, ...p]);
    }
    setEditing(null);
  };

  if (editing !== null) {
    return <BlogEditor post={editing.id ? editing : null} onSave={handleSave} onCancel={() => setEditing(null)} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-['Outfit'] text-xl font-bold text-white">Blog Posts</h2>
          <p className="text-sm text-slate-500 mt-0.5">{posts.length} post{posts.length !== 1 ? 's' : ''}</p>
        </div>
        <button data-testid="blog-new-btn" onClick={() => setEditing({})} className="inline-flex items-center gap-2 bg-[#00E5FF] text-[#0A0A0A] px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-white transition-colors">
          <Plus className="w-4 h-4" />
          New Post
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <div className="w-8 h-8 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-500">Loading posts...</p>
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-white/10 rounded-2xl">
          <FileText className="w-8 h-8 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No blog posts yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <BlogPostRow key={post.id} post={post} onEdit={(p) => setEditing(p)} onDelete={handleDelete} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ========== Admin Home Page ========== */
const ADMIN_SECTIONS = [
  {
    category: 'Insights',
    color: 'from-[#00E5FF]/20 to-cyan-500/5',
    accent: '#00E5FF',
    items: [
      { key: 'enquiries', icon: Mail, name: 'Enquiries', desc: 'View and manage customer messages and quote requests.' },
      { key: 'analytics', icon: BarChart3, name: 'Analytics', desc: 'Real-time traffic, visitors, and performance data.' },
    ],
  },
  {
    category: 'Content',
    color: 'from-blue-500/20 to-blue-500/5',
    accent: '#3B82F6',
    items: [
      { key: 'blog', icon: FileText, name: 'Blog', desc: 'Create and manage blog posts and SEO content.' },
      { key: 'legal', icon: Scale, name: 'Legal', desc: 'Edit your Privacy Policy and Terms of Service.' },
      { key: 'contact', icon: MessageSquare, name: 'Contact Form', desc: 'Customise form dropdowns, options, and defaults.' },
    ],
  },
  {
    category: 'Commerce',
    color: 'from-violet-500/20 to-violet-500/5',
    accent: '#8B5CF6',
    items: [
      { key: 'pricing', icon: CreditCard, name: 'Pricing', desc: 'Edit packages, local launch, bolt-ons, and care plans.' },
      { key: 'products', icon: Package, name: 'Products', desc: 'Manage shop products, finishes, and print zones.' },
      { key: 'orders', icon: Briefcase, name: 'Orders', desc: 'Track and manage customer orders and fulfilment.' },
      { key: 'discounts', icon: Tag, name: 'Discounts', desc: 'Create discount codes and manage the sale banner.' },
    ],
  },
  {
    category: 'Settings',
    color: 'from-emerald-500/20 to-emerald-500/5',
    accent: '#10B981',
    adminOnly: true,
    items: [
      { key: 'accounts', icon: Users, name: 'Accounts', desc: 'Manage team users, roles, and page permissions.' },
    ],
  },
];

function AdminHome({ user, perms, isAdmin, onNavigate }) {
  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div data-testid="admin-home">
      {/* Welcome */}
      <div className="mb-10">
        <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-white tracking-tight">
          {greeting()}, {user?.name || 'Admin'}
        </h1>
        <p className="text-sm text-slate-500 mt-1">What would you like to work on?</p>
      </div>

      {/* Sections */}
      <div className="space-y-10">
        {ADMIN_SECTIONS.map(section => {
          if (section.adminOnly && !isAdmin) return null;
          return (
            <div key={section.category}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-1.5 h-5 rounded-full" style={{ backgroundColor: section.accent }} />
                <h2 className="font-['Outfit'] text-sm font-bold text-white uppercase tracking-wider">{section.category}</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {section.items.map(item => {
                  const hasAccess = isAdmin || perms.includes(item.key);
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.key}
                      data-testid={`home-card-${item.key}`}
                      onClick={() => hasAccess && onNavigate(item.key)}
                      disabled={!hasAccess}
                      className={`relative text-left rounded-2xl p-5 border transition-all duration-300 overflow-hidden group ${
                        hasAccess
                          ? 'bg-[#111111] border-white/[0.06] hover:border-white/15 hover:scale-[1.01] cursor-pointer'
                          : 'bg-[#0C0C0C] border-white/[0.03] cursor-not-allowed opacity-60'
                      }`}
                    >
                      {hasAccess && (
                        <div className={`absolute -top-10 -right-10 w-28 h-28 rounded-full blur-[40px] bg-gradient-to-br ${section.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                      )}
                      <div className="relative">
                        <div className="flex items-center justify-between mb-3">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${section.accent}15` }}>
                            <Icon className="w-5 h-5" style={{ color: hasAccess ? section.accent : '#475569' }} />
                          </div>
                          {!hasAccess && (
                            <div className="flex items-center gap-1.5 bg-white/5 px-2.5 py-1 rounded-full">
                              <ShieldAlert className="w-3 h-3 text-slate-500" />
                              <span className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider">Locked</span>
                            </div>
                          )}
                        </div>
                        <h3 className={`font-['Outfit'] text-base font-semibold mb-1 ${hasAccess ? 'text-white' : 'text-slate-600'}`}>
                          {item.name}
                        </h3>
                        <p className={`text-xs leading-relaxed ${hasAccess ? 'text-slate-500' : 'text-slate-700'}`}>
                          {hasAccess ? item.desc : 'Contact your admin for access to this section.'}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ========== Theme toggle (admin header) ========== */
function ThemeToggle() {
  const theme = useTheme();
  if (!theme) return null;
  const { dark, toggle } = theme;
  return (
    <button
      type="button"
      data-testid="admin-theme-toggle"
      onClick={toggle}
      title={dark ? 'Switch site to light mode' : 'Switch site to dark mode'}
      aria-label="Toggle site theme"
      className="group relative inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] hover:border-[#00E5FF]/40 transition-all"
    >
      {dark
        ? <Sun className="w-3.5 h-3.5 text-amber-300 group-hover:text-amber-200 transition-colors" />
        : <Moon className="w-3.5 h-3.5 text-slate-300 group-hover:text-white transition-colors" />}
      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 group-hover:text-white transition-colors hidden md:inline">
        {dark ? 'Light' : 'Dark'}
      </span>
    </button>
  );
}

/* ========== Dashboard ========== */
function Dashboard({ onLogout, user }) {
  const isAdmin = user?.role === 'admin';
  const perms = isAdmin ? ['enquiries', 'blog', 'pricing', 'products', 'orders', 'discounts', 'legal', 'contact', 'analytics'] : (user?.permissions || []);
  const [tab, setTab] = useState('home');
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchSubmissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/api/admin/submissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { onLogout(); return; }
      const data = await res.json();
      setSubmissions(data);
    } catch (err) {
      console.error('Failed to fetch submissions:', err);
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this submission?')) return;
    const token = localStorage.getItem('admin_token');
    await fetch(`${API}/api/admin/submissions/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    setSubmissions(s => s.filter(sub => sub.id !== id));
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('admin_token');
    await fetch(`${API}/api/admin/auth/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    localStorage.removeItem('admin_token');
    onLogout();
  };

  const filtered = submissions.filter(s =>
    !search || s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.email.toLowerCase().includes(search.toLowerCase()) ||
    (s.message && s.message.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative z-[1] overflow-x-hidden">
      {/* Header */}
      <div className="border-b border-white/5 bg-[#0A0A0A]/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" data-testid="admin-back-link" className="inline-flex items-center gap-1.5 text-slate-500 hover:text-white transition-colors text-[11px] uppercase tracking-wider font-semibold">
              <ArrowLeft className="w-3.5 h-3.5" />
              Exit admin
            </a>
            <div className="w-px h-5 bg-white/10" />
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-[#00E5FF] flex items-center justify-center">
                <span className="font-['Outfit'] font-black text-[#0A0A0A] text-[10px]">Ui</span>
              </div>
              <span className="font-['Outfit'] font-bold text-white text-sm">Admin</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <div className="w-px h-5 bg-white/10" />
            <button data-testid="admin-logout-btn" onClick={handleLogout} className="flex items-center gap-2 text-sm text-slate-500 hover:text-white transition-colors">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Navigation — pill-style breadcrumb when inside a section */}
      {tab !== 'home' && (
        <div className="border-b border-white/5 bg-gradient-to-r from-[#0A0A0A] via-[#00E5FF]/[0.03] to-[#0A0A0A]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
            <button
              data-testid="admin-home-btn"
              onClick={() => setTab('home')}
              className="inline-flex items-center gap-1.5 bg-[#00E5FF]/10 hover:bg-[#00E5FF]/20 text-[#00E5FF] border border-[#00E5FF]/30 hover:border-[#00E5FF]/60 px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
              title="Return to admin dashboard"
            >
              <Home className="w-3.5 h-3.5" />
              Dashboard
            </button>
            <span className="text-slate-600 select-none">›</span>
            <span className="text-xs text-white font-semibold capitalize tracking-wide">{tab}</span>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {tab === 'home' ? (
          <AdminHome user={user} perms={perms} isAdmin={isAdmin} onNavigate={setTab} />
        ) : tab === 'orders' ? (
          <OrderManager />
        ) : tab === 'discounts' ? (
          <DiscountManager />
        ) : tab === 'legal' ? (
          <LegalManager />
        ) : tab === 'pricing' ? (
          <PricingManager />
        ) : tab === 'accounts' && isAdmin ? (
          <AccountManager />
        ) : tab === 'contact' ? (
          <ContactFormManager />
        ) : tab === 'analytics' ? (
          <AnalyticsManager />
        ) : tab === 'products' ? (
          <ProductManager />
        ) : tab === 'blog' ? (
          <BlogManager onLogout={onLogout} />
        ) : (
          <>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div>
                <h1 className="font-['Outfit'] text-2xl font-bold text-white">Enquiries</h1>
                <p className="text-sm text-slate-500 mt-0.5">
                  {submissions.length} submission{submissions.length !== 1 ? 's' : ''} total
                </p>
              </div>
              <div className="relative w-full sm:w-64">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
                <input data-testid="admin-search" type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search enquiries..." className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-2.5 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-[#00E5FF]/50" />
              </div>
            </div>
            {loading ? (
              <div className="text-center py-20">
                <div className="w-8 h-8 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-sm text-slate-500">Loading submissions...</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20 border border-dashed border-white/10 rounded-2xl">
                <Mail className="w-8 h-8 text-slate-600 mx-auto mb-3" />
                <p className="text-sm text-slate-500">{search ? 'No matching enquiries' : 'No enquiries yet'}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filtered.map((sub) => (
                  <SubmissionCard key={sub.id} sub={sub} onDelete={handleDelete} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    document.body.style.background = '#0A0A0A';
    document.documentElement.style.background = '#0A0A0A';
    return () => {
      document.body.style.background = '';
      document.documentElement.style.background = '';
    };
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('admin_token');
      if (!token) { setAuthed(false); return; }
      try {
        const res = await fetch(`${API}/api/admin/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setAuthed(data);
        } else {
          localStorage.removeItem('admin_token');
          setAuthed(false);
        }
      } catch {
        setAuthed(false);
      }
    };
    checkAuth();
  }, []);

  if (authed === null) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!authed) {
    return <LoginForm onLogin={(user) => setAuthed(user)} />;
  }

  return <Dashboard onLogout={() => setAuthed(false)} user={authed} />;
}
